import XRegExp from 'xregexp';

type Validator = ((val:any)=>boolean)|((val:any)=>Promise<boolean>);
type ValidationListener = (errors: string[], forceDirty: boolean)=>void;

export class Validators {
  static required(val:any) {
    if(Array.isArray(val)) {
      return val.length > 0;
    } else {
      return val !== false && val !== null && val !== undefined && val !== '';
    }
  }

  static email(val: any) {
    if(!val) {
      return true;
    } else if(typeof(val) !== 'string') {
      return false;
    }
    return /^(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x21\x23-\x5b\x5d-\x7f]|\\[\x20-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\x21-\x5a\x53-\x7f]|\\[\x20-\x7f])+)\])$/.test(val);
  }

  static phone(val: any) {
    if(!val) {
      return true;
    } else if(typeof(val) !== 'string') {
      return false;
    }
    return /^\+?[\d \-()#]{7,20}$/.test(val);
  }

  static url(val: any) {
    if(!val) {
      return true;
    } else if(typeof(val) !== 'string') {
      return false;
    }
    if(!/^[a-zA-Z]+:\/\//.test(val)) {
      val = 'http://' + val;
    }
    const pattern = new RegExp('^(https?:\\/\\/)?'+ // protocol
                            '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|'+ // domain name
                            '((\\d{1,3}\\.){3}\\d{1,3}))'+ // OR ip (v4) address
                            '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*'+ // port and path
                            '(\\?[;&a-z\\d%_.~+=-]*)?'+ // query string
                            '(\\#[-a-z\\d_]*)?$','i'); // fragment locator
    return pattern.test(val);
  }

  static minLength(minLength: number): Validator {
    return val => {
      if(!val) {
        return false;
      } else if(typeof(val) !== 'string') {
        return false;
      }
      return (val as string).length >= minLength;
    };
  }

  static maxFileSize(maxBytes: number): Validator {
    return val => {
      if(!val) {
        return true;
      } else if(!(val instanceof File)) {
        return true;
      }
      return (val as File).size <= maxBytes;
    };
  }

  static containsNumbers(minCount: number = 1): Validator {
    return Validators.containsCharacters(/\d/, minCount);
  }

  static containsUpperCase(minCount: number = 1): Validator {
    return Validators.containsCharacters(XRegExp('\\p{Lu}'), minCount);
  }

  static containsLowerCase(minCount: number = 1): Validator {
    return Validators.containsCharacters(XRegExp('\\p{Ll}'), minCount);
  }

  private static containsCharacters(regex: RegExp, minCount: number = 1): Validator {
    return val => {
      if(!val) {
        return false;
      } else if(typeof(val) !== 'string') {
        return false;
      }
      const strVal = val as string;
      if(strVal.length < minCount) {
        return false;
      }
      const regexMatches = regex.exec(strVal);
      return !!regexMatches && regexMatches.length >= minCount;
    };
  }

  static isNumber(val: any): boolean {
    if(!val) {
      return true;
    }
    return typeof(val) === 'number' || (typeof(val) === 'string' && parseFloat(val) !== undefined);
  }

  static minValue(min: number): Validator {
    return val => {
      if(val === undefined || val === null || val === '') {
        return true;
      }
      const num = typeof(val) === 'number' ? val : parseFloat(val);
      return num >= min;
    };
  }

  static maxValue(max: number): Validator {
    return val => {
      if(val === undefined || val === null || val === '') {
        return true;
      }
      const num = typeof(val) === 'number' ? val : parseFloat(val);
      return num <= max;
    };
  }
}

class FormField {
  public errors: string[] = [];
  public listeners: ValidationListener[] = [];
  public validators: {[index:string]:Validator} = {};
}

export class ValidationGroup {
  private readonly fields: { [index: string]: FormField };
  private model: any;

  constructor(model: any = null) {
    this.fields = {};
    this.model = model || {};
  }

  public reset(model: any) {
    this.model = model;
    for(const field of Object.values(this.fields)) {
      field.errors.length = 0;
    }
  }

  public get isValid() {
    for (const field of Object.values(this.fields)) {
      if(field.errors.length > 0) {
        return false;
      }
    }
    return true;
  }

  public addField(fieldName: string, validators?: {[index:string]:Validator}) {
    let field = this.fields[fieldName];
    if (!field) {
      field = new FormField();
      this.fields[fieldName] = field;
    }
    field.validators = validators || {};
  }

  public addFieldError(fieldName: string, error: string) {
    const field = this.fields[fieldName];
    if(!field) {
      throw new Error(`Field not found: ${fieldName}`);
    }
    if(field.errors.indexOf(error) < 0) {
      field.errors.push(error);
      for (const listener of field.listeners) {
        listener(field.errors, true);
      }
    }
  }

  public getFieldErrors(name: string): string[] {
    return this.fields[name].errors;
  }

  public async validateField(name: string, forceDirty: boolean = false): Promise<boolean> {
    const field = this.fields[name];
    if (!field) {
      return true;
    }
    if(field.errors.length > 0) {
      field.errors = [];
    }
    if (!field.validators) {
      return true;
    }
    const value = this.model[name];
    for (const errorName in field.validators) {
      const validator = field.validators[errorName];
      const result = validator(value);
      let isValid = false;
      if (typeof(result) === 'boolean') {
        isValid = result;
      } else {
        isValid = await result;
      }
      if(!isValid) {
        field.errors.push(errorName);
      }
    }
    for (const listener of field.listeners) {
      listener(field.errors, forceDirty);
    }
    return !!field.errors;
  }

  public async validate( forceDirty: boolean = false ): Promise<boolean> {
    for (const fieldName of Object.keys(this.fields)) {
      await this.validateField(fieldName, forceDirty);
    }
    return this.isValid;
  }

  public addFieldListener(name: string, listener: ValidationListener) {
    const field = this.fields[name];
    if (!field) {
      this.fields[name] = new FormField();
    }
    this.fields[name].listeners.push(listener);
  }

  public removeFieldListener(name: string, listener: ValidationListener) {
    const field = this.fields[name];
    if (!field) {
      return
    }
    const listeners = this.fields[name].listeners;
    if(!listeners || listeners.length === 0) {
      return;
    }
    const index = listeners.indexOf(listener);
    if(index >= 0) {
      listeners.splice(index, 1);
    }
  }
}